import { Application, Container, Graphics } from "pixi.js";
import {
  DEMO_FORM,
  type KeyMapRound,
  correctDirection,
  keyMapPerseverations,
} from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, darken, lighten, mix, touchMin } from "./paint.js";
import { TOKEN_COLORS } from "./token.js";

/**
 * Drive the car — the key at the top says which way each colour goes, and the key changes.
 *
 * A coloured car arrives, the child presses one of four direction pads, and the car drives off that
 * way. Partway through the round the key changes: the arrows above swap colours, in plain sight,
 * and the colour that went right now goes up.
 *
 * **The key change is animated, and that is the one piece of theatre this game needs.** A key that
 * simply appeared different would be a change a child could miss entirely, and then the game would
 * be measuring whether they happened to glance up — which is not what it is for. The arrows flip
 * with a flash and a sound, so every child gets the same notice, and what differs between them is
 * only what they do next.
 *
 * **The car drives, rather than a tile lighting up.** A direction has to be answered with a
 * direction: pads arranged around the car in the shape of the answer, and a car that visibly leaves
 * that way. A row of four buttons along the bottom would turn a spatial mapping into an arbitrary
 * one, which is a different and much harder task wearing the same clothes.
 */

export interface KeyMapTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateRound: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => KeyMapRound;
  passed: (round: KeyMapRound, chosen: readonly number[]) => boolean;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  startAt?: number;
}

/** Where a direction index points, in canvas coordinates. 0 up, 1 right, 2 down, 3 left. */
const STEP: readonly [number, number][] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

/** A chunky arrow pointing `direction`, centred on the origin. */
function drawArrow(g: Graphics, direction: number, size: number, color: number): void {
  const [dx, dy] = STEP[direction] ?? [0, -1];
  const r = size / 2;
  // Local coordinates with the tip along +1, then mapped onto the direction vector. One arrow
  // rotated four ways rather than four hand-written paths, so no direction is subtly fatter.
  const at = (along: number, across: number): [number, number] => [
    dx * along - dy * across,
    dy * along + dx * across,
  ];
  const shaft = r * 0.3;
  const head = r * 0.62;
  const points: [number, number][] = [
    [r, 0],
    [r - head * 1.1, head],
    [r - head * 1.1, shaft],
    [-r, shaft],
    [-r, -shaft],
    [r - head * 1.1, -shaft],
    [r - head * 1.1, -head],
  ];
  g.poly(points.flatMap(([a, b]) => at(a, b))).fill(color);
}

/** A small car in `color`, facing right. Recognisable at 48 px, which a rectangle is not. */
function drawCar(g: Graphics, color: number, size: number): void {
  const w = size;
  const h = size * 0.58;
  const body = darken(color, 0.08);
  g.roundRect(-w / 2, -h * 0.1, w, h * 0.62, h * 0.24).fill(body);
  // Cabin, set back, so the silhouette reads as a car rather than a brick.
  g.roundRect(-w * 0.28, -h * 0.62, w * 0.58, h * 0.58, h * 0.22).fill(lighten(color, 0.2));
  g.roundRect(-w * 0.2, -h * 0.5, w * 0.42, h * 0.34, h * 0.14).fill({
    color: COLOR.cream,
    alpha: 0.72,
  });
  for (const wx of [-w * 0.26, w * 0.28]) {
    g.circle(wx, h * 0.52, h * 0.22).fill(COLOR.ink);
    g.circle(wx, h * 0.52, h * 0.1).fill(COLOR.cream);
  }
}

export async function runKeyMapTask(opts: RunOptions): Promise<KeyMapTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.58, Math.min(w, h) * 0.46).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /**
   * Runs one whole ROUND and resolves with the direction chosen for each card.
   *
   * A round rather than a card, because the thing being described only exists across cards: what
   * the child did right after the key moved is meaningless without what they were doing before it.
   */
  function present(round: KeyMapRound): {
    chosen: Promise<number[]>;
    pressCorrect: () => void;
    whenReady: () => Promise<void>;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const keyBar = new Container();
    const pads = new Container();
    const car = new Graphics();
    layer.addChild(keyBar, pads, car);

    const chosen: number[] = [];
    let at = 0;
    let accepting = false;
    let resolveChosen: ((c: number[]) => void) | null = null;

    /**
     * Resolved each time a card becomes answerable.
     *
     * The demonstration needs this because a card's setup is not a fixed length: a card that
     * follows a key change waits for the flip to play, which is roughly twice as long. A tutorial
     * pacing itself on a timer therefore pressed into the gap on exactly the card the whole game is
     * about, the press was swallowed by the `accepting` guard, and every press after it answered
     * the wrong card — so the round never finished and the tutorial hung until the child left.
     */
    let readyWaiters: (() => void)[] = [];
    const becomeReady = (): void => {
      accepting = true;
      const waiting = readyWaiters;
      readyWaiters = [];
      for (const resolve of waiting) resolve();
    };

    let padGraphics: Graphics[] = [];
    let keyGraphics: Graphics[] = [];
    let padSize = touchMin();
    let carSize = 64;
    let centre = { x: 0, y: 0 };

    /**
     * The key, as CARS pointing the way they go.
     *
     * **The entries used to be bare coloured arrows, and that is what made the game unreadable to a
     * child.** A red car arrives and the answer is three steps away: notice the car is red, find red
     * in a row of arrows, read that arrow. Nothing on the screen joined the first step to the
     * second — a coloured arrow is not a car, and a five-year-old does not treat "the red one" as an
     * abstraction that spans two different pictures.
     *
     * So each entry now shows the actual car in its colour with its arrow beneath it. The child
     * matches a picture to the same picture, which is a thing they can do, and the lookup that is
     * genuinely part of the task — which arrow does this one carry — is all that is left of it.
     */
    function drawKeyBar(): void {
      const key = round.keys[at] ?? round.keys[0] ?? [];
      keyGraphics.forEach((g, colour) => {
        const direction = key[colour];
        if (direction === undefined) return;
        const swatch = TOKEN_COLORS[colour] ?? COLOR.sunny;
        const w = padSize * 0.78;
        const h = padSize * 1.02;
        g.clear();
        g.roundRect(-w / 2, -h / 2, w, h, w * 0.24).fill(mix(swatch, COLOR.stage, 0.68));
        const car = new Graphics();
        drawCar(car, swatch, w * 0.74);
        car.position.set(0, -h * 0.2);
        const arrow = new Graphics();
        drawArrow(arrow, direction, w * 0.5, swatch);
        arrow.position.set(0, h * 0.28);
        for (const child of g.removeChildren()) child.destroy();
        g.addChild(car, arrow);
      });
    }

    function drawPads(): void {
      padGraphics.forEach((g, direction) => {
        g.clear();
        g.roundRect(-padSize / 2, -padSize / 2, padSize, padSize, padSize * 0.3).fill(
          mix(COLOR.stage, COLOR.card, 0.14),
        );
        drawArrow(g, direction, padSize * 0.66, mix(COLOR.cream, COLOR.stage, 0.25));
      });
    }

    function drawCard(): void {
      car.clear();
      const colour = round.cards[at];
      if (colour === undefined) return;
      drawCar(car, TOKEN_COLORS[colour] ?? COLOR.sunny, carSize);
      car.position.set(centre.x, centre.y);
      car.alpha = 1;
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const topReserved = 58;

      padSize = Math.max(touchMin(), Math.min(w * 0.2, 92));
      carSize = Math.min(w * 0.24, 96);
      const gap = padSize * 0.24;

      // The entry is a card carrying a car above an arrow, so it is taller than it is wide.
      const keySize = padSize * 0.78;
      const keyH = padSize * 1.02;
      const keyW = keySize * round.colours + gap * (round.colours - 1);
      const keyY = topReserved + keyH / 2;

      // The pads sit around the car in the shape of the answer, so pressing "up" is a movement
      // upwards. That correspondence is the difference between a spatial mapping and an arbitrary
      // one, and the arbitrary version is a much harder task wearing the same clothes.
      /**
       * The car and its pads, centred in what is left under the key.
       *
       * The first version derived this from the key's position plus a fraction of the remainder,
       * which put the whole cluster in the upper half and left a third of the screen empty below it
       * — on a phone the pads finished around six hundred pixels up an eight-hundred-pixel screen.
       * Measuring the space that is actually left and sitting in the middle of it is the same
       * arithmetic every other game here does.
       */
      const cluster = padSize * 1.6;
      const keyBottom = keyY + keyH / 2;
      const room = h - keyBottom - gap * 2;
      centre = { x: w / 2, y: keyBottom + room / 2 };

      for (const child of keyBar.removeChildren()) child.destroy();
      keyGraphics = [];
      const bar = new Graphics();
      bar
        .roundRect(w / 2 - keyW / 2 - gap, keyY - keyH / 2 - gap * 0.7, keyW + gap * 2, keyH + gap * 1.4, keySize * 0.34)
        .fill({ color: 0xffffff, alpha: 0.06 })
        .stroke({ width: 2, color: accent, alpha: 0.4 });
      keyBar.addChild(bar);
      for (let colour = 0; colour < round.colours; colour++) {
        const g = new Graphics();
        g.position.set(w / 2 - keyW / 2 + keySize / 2 + colour * (keySize + gap), keyY);
        keyBar.addChild(g);
        keyGraphics.push(g);
      }

      for (const child of pads.removeChildren()) child.destroy();
      padGraphics = [];
      for (let direction = 0; direction < STEP.length; direction++) {
        const [dx, dy] = STEP[direction] as [number, number];
        const g = new Graphics();
        g.eventMode = "static";
        g.cursor = "pointer";
        g.position.set(centre.x + dx * cluster, centre.y + dy * cluster);
        const half = padSize / 2 + padSize * 0.1;
        g.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", () => onPress(direction));
        pads.addChild(g);
        padGraphics.push(g);
      }

      drawKeyBar();
      drawPads();
      drawCard();
    }

    /**
     * The key changing, announced.
     *
     * Every child gets the same notice, which is what stops the game measuring whether they
     * happened to be looking up. What it does NOT do is say which colour went where — the arrows
     * simply turn over, and reading them is the task.
     */
    async function announceSwitch(): Promise<void> {
      tick();
      const w = app.renderer.width;
      glow(glowLayer, w / 2, keyGraphics[0]?.position.y ?? 80, padSize * 1.4, accent, 620);
      if (prefersReducedMotion()) {
        drawKeyBar();
        await idle(420);
        return;
      }
      // A flip: squashed to nothing, redrawn with the new key, opened out again.
      await animate(160, (t) => {
        for (const g of keyGraphics) g.scale.y = 1 - easeOutCubic(t);
      });
      drawKeyBar();
      await animate(240, (t) => {
        for (const g of keyGraphics) g.scale.y = easeOutBack(t);
      });
      await idle(320);
    }

    /** One card: show it, wait for a press, drive off, score nothing. */
    async function runCard(): Promise<void> {
      if (round.switchPoints.includes(at) && at > 0) await announceSwitch();
      drawCard();
      if (!prefersReducedMotion()) {
        car.scale.set(0);
        await animate(260, (t) => car.scale.set(easeOutBack(t)));
      }
      becomeReady();
    }

    async function drive(direction: number, correct: boolean): Promise<void> {
      const [dx, dy] = STEP[direction] as [number, number];
      const distance = padSize * 2.6;
      if (correct) {
        cellTone(direction + 2);
        burst(fx, centre.x + dx * padSize, centre.y + dy * padSize, [accent, COLOR.correct], 10);
      } else {
        missTone();
        await nudge(car, 6);
      }
      await animate(320, (t) => {
        const e = easeOutCubic(t);
        car.position.set(centre.x + dx * distance * e, centre.y + dy * distance * e);
        car.alpha = 1 - e * 0.9;
      });
    }

    function onPress(direction: number): void {
      if (!accepting) return;
      accepting = false;
      const g = padGraphics[direction];
      if (g) {
        void pop(g, { scale: 1.12 });
        ripple(fx, g.position.x, g.position.y, accent, padSize);
      }

      const wanted = correctDirection(round, at);
      const correct = direction === wanted;
      chosen.push(direction);

      void (async () => {
        await drive(direction, correct);
        if (!correct) {
          /**
           * The right answer is shown, on the key rather than on the pads.
           *
           * A child who went the wrong way here almost always went the way the OLD key said, so
           * pointing at the correct pad would teach them the answer to this card and nothing else.
           * Lighting the key entry they should have read teaches them where to look next time,
           * which is the actual lesson.
           */
          const colour = round.cards[at];
          const swatch = keyGraphics[colour ?? 0];
          if (swatch) {
            glow(glowLayer, swatch.position.x, swatch.position.y, padSize * 0.7, COLOR.correct, 700);
            await pop(swatch, { scale: 1.25 });
          }
          await idle(420);
        }
        at++;
        if (at >= round.cards.length) {
          successChime();
          await idle(320);
          resolveChosen?.([...chosen]);
          return;
        }
        await idle(180);
        await runCard();
      })();
    }

    layout();
    app.renderer.on("resize", layout);
    void runCard();

    return {
      chosen: new Promise<number[]>((resolve) => {
        resolveChosen = resolve;
      }),
      /**
       * Lets the demonstration play the round itself. The rule is shown, never stated.
       *
       * It presses the answer for whatever card is CURRENT rather than for an index the caller is
       * counting, so the two cannot drift apart however long a card takes to arrive.
       */
      pressCorrect: () => onPress(correctDirection(round, at)),
      whenReady: () =>
        accepting ? Promise.resolve() : new Promise<void>((r) => readyWaiters.push(r)),
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: KeyMapTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Two things are invisible and neither can be said on a surface with no text: that the strip at
   * the top decides where the car goes, and — the one that matters — that it CHANGES partway
   * through. A child who never sees a change coming will meet their first one in a scored round.
   *
   * So the demonstration plays a whole short round correctly, key change included. It is the
   * longest tutorial in the bundle and it has to be: a rule about what happens LATER cannot be
   * shown in one card.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateRound(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      for (let card = 0; card < demo.cards.length; card++) {
        if (opts.signal?.aborted) break;
        // Waits for the card to be answerable rather than guessing how long it took to arrive.
        await shown.whenReady();
        await idle(card === 0 ? 900 : 620);
        if (opts.signal?.aborted) break;
        shown.pressCorrect();
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.chosen, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const round = opts.generateRound(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The cards, then every key in force, flattened. Both are needed: the same card means a
        // different answer under a different key, which is the whole game.
        stimulus: [round.colours, ...round.cards, ...round.keys.flat()],
        options: [round.switchPoints],
        correctIndex: round.cards.length,
      });

      const start = performance.now();
      const trial = present(round);
      const outcome = await untilSettled(trial.chosen, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.passed(round, outcome);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: outcome,
        correct,
        responseMs: elapsed,
        /**
         * Cards sent the way the REPLACED key said.
         *
         * The measure this game exists for, and it is deliberately not the same as "wrong". A child
         * who guessed and a child whose hand carried on doing what it had been doing are both wrong
         * and they are not the same thing, so only the second is counted here.
         */
        corrections: keyMapPerseverations(round, outcome),
      });

      const fast = detectImplausibleSpeed(elapsed, round.cards.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
